import { useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const IMG_BEACON_HERO = `${process.env.PUBLIC_URL}/images/${encodeURIComponent("Beacon -town.jpg")}`;
const IMG_TRAVEL_FALLBACK = `${process.env.PUBLIC_URL}/images/storm-king-art.jpg`;

const IMG_COLD_SPRING = `${process.env.PUBLIC_URL}/images/${encodeURIComponent("Cold-spring.jpg")}`;

const DEFAULT_TITLE = "Sofia & Jason";
const PAGE_TITLE = "Travel | Sofia & Jason";

const artVenues = [
  {
    tag: "Contemporary",
    title: "Dia Beacon",
    href: "https://www.diabeacon.org/",
    body: "A former Nabisco box-printing plant turned into a cathedral of minimalist and conceptual art. 300,000 square feet of skylit space.  This is a cannot miss for those staying in Beacon!",
    offset: false,
  },
  {
    tag: "Sculpture",
    title: "Art Omi",
    href: "https://artomi.org/",
    body: "120 acres of rolling hills featuring large-scale architecture and sculpture installations that challenge the horizon.",
    offset: true,
  },
  {
    tag: "Italianate",
    title: "Magazzino",
    href: "https://magazzino.art/",
    body: "Dedicated to Arte Povera. A stunning architectural achievement nestled in the Cold Spring woods.",
    offset: false,
  },
];

const mobileNavClass = ({ isActive }) =>
  [
    "flex flex-col items-center justify-center p-4 transition-all duration-300",
    isActive
      ? "bg-[#F2C12E]/20 text-[#8B1A2B] dark:text-red-400"
      : "text-stone-400 hover:bg-[#A8C8E8]/30 active:translate-y-[-2px] dark:text-stone-500",
  ].join(" ");

function MobileNavFaqLink({ label, icon }) {
  const location = useLocation();
  const isActive =
    location.pathname === "/travel" && location.hash === "#faq";
  const className = [
    "flex flex-col items-center justify-center p-4 transition-all duration-300",
    isActive
      ? "bg-[#F2C12E]/20 text-[#8B1A2B] dark:text-red-400"
      : "text-stone-400 hover:bg-[#A8C8E8]/30 active:translate-y-[-2px] dark:text-stone-500",
  ].join(" ");

  return (
    <Link
      to="/travel#faq"
      className={className}
      aria-current={isActive ? "page" : undefined}
    >
      <span className="material-symbols-outlined">{icon}</span>
      <span className="mt-1 font-['Plus_Jakarta_Sans'] text-[10px] uppercase tracking-widest">
        {label}
      </span>
    </Link>
  );
}

export default function TravelPage() {
  const location = useLocation();

  useEffect(() => {
    document.title = PAGE_TITLE;
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, []);

  useEffect(() => {
    if (location.hash !== "#faq") return;
    const t = window.setTimeout(() => {
      document.getElementById("faq")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.hash]);

  return (
    <>
      <main className="selection:bg-tertiary-fixed-dim selection:text-on-tertiary-fixed pb-24 pt-28 md:pb-0 md:pt-32">
        {/* Hero */}
        <section className="relative flex min-h-[560px] items-center overflow-x-hidden bg-surface px-8 py-10 md:min-h-[716px] md:px-20 md:py-16">
          <div className="z-10 grid w-full grid-cols-1 gap-8 md:grid-cols-12">
            <div className="md:col-span-7">
              <h1 className="mb-8 font-headline text-6xl leading-[0.95] text-primary-container md:text-9xl md:leading-[0.98]">
                Travel &amp; <br />
                <span className="font-light italic">FAQ</span>
              </h1>
              <div
                className="max-w-xl border-l-4 border-tertiary-fixed-dim pl-8 font-body text-xl leading-relaxed opacity-80 min-h-[0.5rem]"
                aria-hidden
              />
            </div>
            <div className="relative mt-12 md:col-span-5 md:mt-0">
              <div className="aspect-[4/5] w-full overflow-hidden bg-secondary-fixed-dim shadow-2xl grayscale transition-all duration-700 hover:grayscale-0">
                <img
                  src={IMG_BEACON_HERO}
                  alt="Historic brick buildings and storefronts in Beacon, NY, with green trees and a clear sky"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = IMG_TRAVEL_FALLBACK;
                  }}
                />
              </div>
              <div className="absolute -bottom-8 -left-8 hidden bg-tertiary-fixed-dim p-8 md:block">
                <span className="font-headline text-2xl uppercase tracking-widest text-on-tertiary-fixed">
                  BEACON, NY
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Art scene */}
        <section className="bg-secondary-fixed-dim px-8 py-32 md:px-20">
          <div className="flex flex-col items-start gap-20 md:flex-row">
            <div className="top-32 md:sticky md:w-1/3">
              <h2 className="mb-4 font-headline text-4xl uppercase tracking-[0.2em] text-on-secondary-fixed">
                The Art Scene
              </h2>
              <div className="mb-8 h-1 w-24 bg-primary-container" />
              <p className="font-body leading-relaxed text-on-secondary-fixed-variant">
                The Hudson Valley's art scene extends well beyond Storm King.
              </p>
            </div>
            <div className="grid w-full grid-cols-1 gap-12 md:grid-cols-2 md:w-2/3">
              {artVenues.map((venue) => (
                <a
                  key={venue.title}
                  href={venue.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={[
                    "flex aspect-square flex-col justify-between bg-surface-container-lowest p-10 transition-transform duration-500 hover:translate-y-[-10px]",
                    venue.offset ? "md:mt-24" : "",
                  ].join(" ")}
                >
                  <div>
                    <span className="mb-4 block font-label text-xs uppercase tracking-widest text-primary">
                      {venue.tag}
                    </span>
                    <h3 className="mb-6 font-headline text-3xl text-on-surface">
                      {venue.title}
                    </h3>
                    <p className="text-sm leading-relaxed opacity-70">
                      {venue.body}
                    </p>
                  </div>
                  <div className="mt-8">
                    <span
                      className="material-symbols-outlined text-primary-container"
                      aria-hidden
                    >
                      arrow_forward
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Beacon / brew / Cold Spring bento */}
        <section className="bg-surface px-8 py-32 md:px-20">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="border-l-8 border-primary-container bg-surface-container-low p-12 md:col-span-2 md:row-span-2">
              <h2 className="mb-8 font-headline text-5xl text-on-surface">
                Beacon
              </h2>
              <ul className="space-y-8 font-body">
                <li>
                  <span className="block text-lg font-bold text-tertiary-container">
                    Main Street
                  </span>
                  <span className="text-sm opacity-60">
                    Curated boutiques, rare vinyl, and the best coffee north of
                    NYC.
                  </span>
                </li>
                <li>
                  <span className="block text-lg font-bold text-tertiary-container">
                    Mount Beacon Hike
                  </span>
                  <span className="text-sm opacity-60">
                    A steep incline for the brave. Rewarded with panoramic views
                    of the Hudson River.
                  </span>
                </li>
                <li>
                  <span className="block text-lg font-bold text-tertiary-container">
                    The Roundhouse
                  </span>
                  <span className="text-sm opacity-60">
                    Dinner by the waterfall. Brutalist brick meets rushing water.
                  </span>
                </li>
              </ul>
            </div>
            <div className="flex flex-col justify-center bg-primary-container p-12 text-on-primary md:col-span-2">
              <h2 className="mb-6 font-headline text-3xl uppercase tracking-widest">
                The Brew List
              </h2>
              <div className="flex flex-col gap-6">
                <div className="border-b border-on-primary/20 pb-4">
                  <h4 className="text-lg font-bold">Hudson Valley Brewery</h4>
                  <p className="mt-1 text-xs uppercase tracking-widest opacity-70">
                    Sour IPAs &amp; Graphic Art
                  </p>
                </div>
                <div>
                  <h4 className="text-lg font-bold">Industrial Arts</h4>
                  <p className="mt-1 text-xs uppercase tracking-widest opacity-70">
                    Wrench Series &amp; Skyline Views
                  </p>
                </div>
              </div>
            </div>
            <div className="min-h-[200px] overflow-hidden bg-tertiary-fixed-dim md:col-span-1">
              <img
                src={IMG_COLD_SPRING}
                alt="Hudson River valley with forested hills and a train along the shore near Cold Spring"
                className="h-full min-h-[200px] w-full object-cover grayscale md:min-h-full"
              />
            </div>
            <div className="flex flex-col justify-end bg-surface-container-highest p-10 md:col-span-1">
              <h2 className="mb-4 font-headline text-3xl text-on-surface">
                Cold Spring
              </h2>
              <p className="text-xs leading-relaxed opacity-70">
                Antiques and the &apos;Ruins&apos; hike at Cornish Estate. A
                slower tempo.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section
          className="relative bg-surface-container px-8 py-32 md:px-20"
          id="faq"
        >
          <div className="absolute top-0 right-0 -z-0 h-full w-1/3 bg-secondary-fixed-dim/20" />
          <div className="relative z-10">
            <div className="relative mb-24">
              <p className="mb-4 font-label text-sm uppercase tracking-[0.5em] text-primary">
                Guest information
              </p>
              <span
                className="pointer-events-none absolute -top-12 left-0 select-none font-headline text-7xl leading-none text-on-surface opacity-10 md:text-9xl"
                aria-hidden
              >
                FAQ
              </span>
              <h2 className="relative font-headline text-5xl text-primary-container">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-x-24 gap-y-16 md:grid-cols-2">
              <div className="border-b border-primary-container/20 pb-8">
                <h3 className="mb-4 font-headline text-xl text-primary">
                  How do I get there?
                </h3>
                <div className="space-y-4 font-body text-sm leading-relaxed opacity-80">
                  <p className="italic">
                    Storm King is accessible by Metro-North Railroad and NJ
                    Transit.
                  </p>
                  <p>
                  A private shuttle will run between behind Yankee Clipper Diner (397 Main St, Beacon, NY) and Storm King on the wedding day (about a 30-minute ride). If you're coming from NYC, we recommend taking Metro-North from Grand Central to Beacon and hopping on the shuttle from there.
                  </p>
                  <p>
                    <span className="font-semibold not-italic">NJ Transit:</span>{" "}
                    Penn Station (transfer at Secaucus Junction) or Hoboken to
                    Salisbury Mills-Cornwall. Cornwall station is approximately
                    10 minutes from Storm King by car.
                  </p>
                  <p>
                    For those in Beacon, a private shuttle will depart from the
                    behind Yankee Clipper Diner (397 Main St, Beacon, NY) to Storm King at 2:45 PM.
                  </p>
                  <p>
                    <span className="font-semibold not-italic">By car:</span>{" "}
                    Onsite parking will be available for those coming by car.
                  </p>
                </div>
              </div>
              <div className="border-b border-primary-container/20 pb-8">
                <h3 className="mb-4 font-headline text-xl text-primary">
                  What should I wear?
                </h3>
                <p className="font-body text-sm leading-relaxed opacity-80">
                Sculptural chic attire, inspired by the beauty we’ll be surrounded by! 
                Think elevated, creative, and a little unexpected. This is your invitation 
                to have fun with it! We're spending the evening on the grounds of an open-air sculpture park, so please wear shoes suitable for grass. 
                </p>
              </div>
              <div className="border-b border-primary-container/20 pb-8">
                <h3 className="mb-4 font-headline text-xl text-primary">
                  Are children welcome?
                </h3>
                <p className="font-body text-sm leading-relaxed opacity-80">
                We adore your little ones! Our wedding will be adults-only (aside from our nieces and nephew), 
                but we'd love for your children to join us at the Welcome Party on Friday night.
                </p>
              </div>
              <div className="border-b border-primary-container/20 pb-8">
                <h3 className="mb-4 font-headline text-xl text-primary">
                  Do you have a registry?
                </h3>
                <p className="font-body text-sm leading-relaxed opacity-80">
                  Your presence is truly our present. If you feel inclined to give
                  a gift, we don&apos;t have a formal registry but do have a{" "}
                  <Link
                    to="/registry"
                    className="underline decoration-primary-container/50 underline-offset-2 hover:text-primary"
                  >
                    honeymoon, home, &amp; future adventure
                  </Link>{" "}
                  fund!
                </p>
              </div>
              <div className="border-b border-primary-container/20 pb-8 md:col-span-2">
                <h3 className="mb-4 font-headline text-xl text-primary">
                  Can I take photos?
                </h3>
                <p className="font-body text-sm leading-relaxed opacity-80">
                  Snap away — just be present and enjoy the day with us.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="flex flex-col items-center justify-center bg-surface py-40">
          <h2 className="mb-12 max-w-2xl px-8 text-center font-headline text-3xl leading-tight md:text-5xl text-on-surface">
            We look forward to seeing you in the Valley.
          </h2>
          <Link
            to="/details#rsvp"
            className="bg-primary-container px-16 py-6 font-headline text-lg uppercase tracking-widest text-on-primary transition-colors duration-200 hover:bg-primary active:scale-95"
          >
            Confirm your attendance
          </Link>
        </section>
      </main>

      {/* Mobile bottom bar — maps to site routes */}
      <nav
        className="fixed bottom-0 left-0 z-50 flex h-20 w-full items-center justify-around border-t-[0.5px] border-[#8B1A2B]/10 bg-[#F5EDD8] px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 dark:bg-stone-900 md:hidden"
        aria-label="Section shortcuts"
      >
        <NavLink to="/about" className={mobileNavClass}>
          <span className="material-symbols-outlined">auto_awesome</span>
          <span className="mt-1 font-['Plus_Jakarta_Sans'] text-[10px] uppercase tracking-widest">
            Story
          </span>
        </NavLink>
        <NavLink to="/details" className={mobileNavClass}>
          <span className="material-symbols-outlined">calendar_today</span>
          <span className="mt-1 font-['Plus_Jakarta_Sans'] text-[10px] uppercase tracking-widest">
            Schedule
          </span>
        </NavLink>
        <NavLink
          to="/travel"
          end
          className={() =>
            mobileNavClass({
              isActive:
                location.pathname === "/travel" &&
                location.hash !== "#faq",
            })
          }
        >
          <span className="material-symbols-outlined">explore</span>
          <span className="mt-1 font-['Plus_Jakarta_Sans'] text-[10px] uppercase tracking-widest">
            Travel
          </span>
        </NavLink>
        <MobileNavFaqLink label="FAQ" icon="help_outline" />
        <NavLink to="/details#rsvp" className={mobileNavClass}>
          <span className="material-symbols-outlined">favorite</span>
          <span className="mt-1 font-['Plus_Jakarta_Sans'] text-[10px] uppercase tracking-widest">
            RSVP
          </span>
        </NavLink>
      </nav>
    </>
  );
}
